import type { WorkspaceView } from "./navigationState";

export type WorkspaceChunkKey =
  | "legacy-shell"
  | "patients"
  | "organizations"
  | "employees"
  | "crm"
  | "schedules"
  | "nomenclature"
  | "medical-visit"
  | "references"
  | "template-manager"
  | "bot-scenarios"
  | "platform-admin";

export interface LazyWorkspaceBoundary {
  view: WorkspaceView;
  chunkKey: WorkspaceChunkKey;
  ownsPatientData: boolean;
  requiresClinicContext: boolean;
  migrationStage: "legacy-mounted" | "typed-contract-ready" | "react-lazy-ready";
}

export const lazyWorkspaceBoundaries: LazyWorkspaceBoundary[] = [
  { view: "patients", chunkKey: "patients", ownsPatientData: true, requiresClinicContext: true, migrationStage: "legacy-mounted" },
  { view: "patient", chunkKey: "patients", ownsPatientData: true, requiresClinicContext: true, migrationStage: "legacy-mounted" },
  { view: "new", chunkKey: "patients", ownsPatientData: true, requiresClinicContext: true, migrationStage: "legacy-mounted" },
  { view: "draft", chunkKey: "patients", ownsPatientData: true, requiresClinicContext: true, migrationStage: "legacy-mounted" },
  { view: "organizations", chunkKey: "organizations", ownsPatientData: false, requiresClinicContext: true, migrationStage: "legacy-mounted" },
  { view: "organization", chunkKey: "organizations", ownsPatientData: false, requiresClinicContext: true, migrationStage: "legacy-mounted" },
  { view: "employees", chunkKey: "employees", ownsPatientData: false, requiresClinicContext: true, migrationStage: "legacy-mounted" },
  { view: "employee", chunkKey: "employees", ownsPatientData: false, requiresClinicContext: true, migrationStage: "legacy-mounted" },
  { view: "crm", chunkKey: "crm", ownsPatientData: true, requiresClinicContext: true, migrationStage: "typed-contract-ready" },
  { view: "schedules", chunkKey: "schedules", ownsPatientData: true, requiresClinicContext: true, migrationStage: "typed-contract-ready" },
  { view: "nomenclature", chunkKey: "nomenclature", ownsPatientData: false, requiresClinicContext: true, migrationStage: "typed-contract-ready" },
  { view: "visits", chunkKey: "medical-visit", ownsPatientData: true, requiresClinicContext: true, migrationStage: "typed-contract-ready" },
  { view: "medicalVisit", chunkKey: "medical-visit", ownsPatientData: true, requiresClinicContext: true, migrationStage: "typed-contract-ready" },
  { view: "references", chunkKey: "references", ownsPatientData: false, requiresClinicContext: false, migrationStage: "typed-contract-ready" },
  { view: "reference", chunkKey: "references", ownsPatientData: false, requiresClinicContext: false, migrationStage: "typed-contract-ready" },
  { view: "templateManager", chunkKey: "template-manager", ownsPatientData: false, requiresClinicContext: false, migrationStage: "typed-contract-ready" },
  { view: "botScenarios", chunkKey: "bot-scenarios", ownsPatientData: false, requiresClinicContext: true, migrationStage: "typed-contract-ready" },
  { view: "dashboard", chunkKey: "platform-admin", ownsPatientData: false, requiresClinicContext: false, migrationStage: "typed-contract-ready" },
  { view: "documents", chunkKey: "patients", ownsPatientData: true, requiresClinicContext: true, migrationStage: "legacy-mounted" },
  { view: "marketplace", chunkKey: "platform-admin", ownsPatientData: false, requiresClinicContext: false, migrationStage: "typed-contract-ready" },
  { view: "platformCrm", chunkKey: "platform-admin", ownsPatientData: false, requiresClinicContext: false, migrationStage: "typed-contract-ready" },
  { view: "platform-admin", chunkKey: "platform-admin", ownsPatientData: false, requiresClinicContext: false, migrationStage: "typed-contract-ready" },
];

export function lazyBoundaryForView(view: WorkspaceView | string | undefined): LazyWorkspaceBoundary {
  return lazyWorkspaceBoundaries.find((boundary) => boundary.view === view)
    ?? { view: "dashboard", chunkKey: "legacy-shell", ownsPatientData: false, requiresClinicContext: false, migrationStage: "legacy-mounted" };
}

export function workspaceCanBypassClinicContext(view: WorkspaceView | string | undefined): boolean {
  return !lazyBoundaryForView(view).requiresClinicContext;
}

export function workspacesReadyForReactLazy(): LazyWorkspaceBoundary[] {
  return lazyWorkspaceBoundaries.filter((boundary) => boundary.migrationStage !== "legacy-mounted");
}
